import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { Star } from '@mui/icons-material';

// Modern animations
const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const progressFill = keyframes`
  from {
    width: 0;
  }
  to {
    width: var(--target-width);
  }
`;

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '16px 0',
  [theme.breakpoints.down('sm')]: {
    padding: '12px 0'
  }
}));

const RatingRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
  animation: `${slideInLeft} 0.6s ease-out`,
  '&:last-child': {
    marginBottom: 0
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '10px'
  }
}));

const StarLabel = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: '60px',
  gap: '4px',
  marginRight: '12px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '50px',
    marginRight: '8px'
  }
}));

const StarIcon = styled(Star)(({ theme }) => ({
  fontSize: '16px',
  color: '#FFC107',
  [theme.breakpoints.down('sm')]: {
    fontSize: '14px'
  }
}));

const RatingText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 500,
  color: '#121927',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '20px',
  backgroundColor: '#F3F4F6',
  borderRadius: '10px',
  overflow: 'hidden',
  position: 'relative',
  marginRight: '12px',
  [theme.breakpoints.down('sm')]: {
    height: '16px',
    marginRight: '8px',
    borderRadius: '8px'
  }
}));

const ProgressBar = styled(Box)(({ theme, percentage, color, delay }) => ({
  height: '100%',
  borderRadius: '10px',
  background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`,
  position: 'relative',
  animation: `${progressFill} 1s ease-out ${delay}s both`,
  '--target-width': `${percentage}%`,
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
    borderRadius: '10px'
  }
}));

const CountLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#666',
  minWidth: '30px',
  textAlign: 'right',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px',
    minWidth: '25px'
  }
}));

const SummaryContainer = styled(Box)(({ theme }) => ({
  marginTop: '20px',
  padding: '16px',
  backgroundColor: '#F8FAFC',
  borderRadius: '12px',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  [theme.breakpoints.down('sm')]: {
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px'
  }
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
  '&:last-child': {
    marginBottom: 0
  }
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#666',
  [theme.breakpoints.down('sm')]: {
    fontSize: '12px'
  }
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 600,
  color: '#121927',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px'
  }
}));

const EnhancedReviewsChart = ({ reputationData }) => {
  const data = reputationData || {
    yourBusiness: { rating: 4.2, reviewCount: 44 },
    reviews: []
  };

  // Normalize reviews array from possible shapes
  const reviewsArray = Array.isArray(data.reviews) ? data.reviews : (data.reviews?.reviews || []);

  // Convert Google's enum rating to number
  const toNumeric = (star) => {
    if (typeof star === 'number') return star;
    const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
    return map[String(star || '').toUpperCase()] || 0;
  };

  // Build counts per star either from real reviews or estimate from avg/total
  const buildCounts = (avgRating, totalReviews) => {
    const ratings = [5, 4, 3, 2, 1];
    
    // Use actual reviews when available
    if (reviewsArray.length > 0) {
      const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      reviewsArray.forEach(r => {
        const n = Math.round(toNumeric(r.starRating));
        if (n >= 1 && n <= 5) counts[n]++;
      });
      return ratings.map(rating => ({ rating, count: counts[rating] }));
    }

    // Fallback estimation using avg and total
    const safeRating = Number(avgRating) || 4.2;
    const safeTotal = Number(totalReviews) || 0;
    
    if (safeTotal === 0) {
      return ratings.map(rating => ({ rating, count: 0 }));
    }

    const percentages = {};
    ratings.forEach(r => {
      if (r === Math.floor(safeRating)) percentages[r] = 0.4;
      else if (Math.abs(r - safeRating) === 1) percentages[r] = 0.25;
      else percentages[r] = Math.max(0.05, 0.1 / Math.max(1, Math.abs(r - safeRating)));
    });
    
    return ratings.map(rating => ({
      rating,
      count: Math.max(0, Math.floor(safeTotal * percentages[rating]))
    }));
  };

  const starData = buildCounts(data.yourBusiness.rating, data.yourBusiness.reviewCount);
  const totalReviews = starData.reduce((sum, item) => sum + item.count, 0);
  const maxCount = Math.max(1, ...starData.map(item => item.count));

  // Color scheme for different star ratings
  const getStarColor = (rating) => {
    switch (rating) {
      case 5: return '#34A853'; // Green
      case 4: return '#0B91D6'; // Blue
      case 3: return '#F59E0B'; // Yellow/Orange
      case 2: return '#EF4444'; // Red
      case 1: return '#DC2626'; // Dark Red
      default: return '#9CA3AF'; // Gray
    }
  };

  // Calculate percentage and positive/negative ratios
  const positiveReviews = starData.filter(item => item.rating >= 4).reduce((sum, item) => sum + item.count, 0);
  const negativeReviews = starData.filter(item => item.rating <= 2).reduce((sum, item) => sum + item.count, 0);
  const neutralReviews = starData.find(item => item.rating === 3)?.count || 0;

  const positivePercentage = totalReviews > 0 ? Math.round((positiveReviews / totalReviews) * 100) : 0;
  const negativePercentage = totalReviews > 0 ? Math.round((negativeReviews / totalReviews) * 100) : 0;

  return (
    <ChartContainer>
      {/* Star Rating Breakdown */}
      <Stack spacing={1}>
        {starData.map((item, index) => {
          const percentage = totalReviews > 0 ? (item.count / maxCount) * 100 : 0;
          const actualPercentage = totalReviews > 0 ? Math.round((item.count / totalReviews) * 100) : 0;
          
          return (
            <RatingRow key={item.rating} style={{ animationDelay: `${index * 0.1}s` }}>
              <StarLabel>
                <StarIcon />
                <RatingText>{item.rating}</RatingText>
              </StarLabel>
              
              <ProgressContainer>
                <ProgressBar 
                  percentage={percentage}
                  color={getStarColor(item.rating)}
                  delay={index * 0.1}
                />
              </ProgressContainer>
              
              <CountLabel>{item.count}</CountLabel>
            </RatingRow>
          );
        })}
      </Stack>

      {/* Summary Statistics */}
      {totalReviews > 0 && (
        <SummaryContainer>
          <SummaryRow>
            <SummaryLabel>Positive Reviews (4-5★)</SummaryLabel>
            <SummaryValue style={{ color: '#34A853' }}>{positivePercentage}%</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Neutral Reviews (3★)</SummaryLabel>
            <SummaryValue style={{ color: '#F59E0B' }}>{Math.round((neutralReviews / totalReviews) * 100)}%</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>Negative Reviews (1-2★)</SummaryLabel>
            <SummaryValue style={{ color: '#EF4444' }}>{negativePercentage}%</SummaryValue>
          </SummaryRow>
        </SummaryContainer>
      )}
    </ChartContainer>
  );
};

export default EnhancedReviewsChart;
